import { useMemo, ReactNode } from 'react';
import { BaseOption } from 'ui/selects';
import { useTranslation } from 'modules/queryBuilder/hooks/useTranslation';
import { ClientDataType } from 'modules/queryBuilder/types/clientDataType';
import { EXCLUDED_WHATSAPP_EVENTS } from 'modules/queryBuilder/renderers/constants';
import { DATA_TYPE_TO_ICON } from 'modules/queryBuilder/constants';
import { useFilterNonFeatureFlaggedEvents } from 'modules/queryBuilder/renderers/hooks/useFilterNonFeatureFlaggedEvents';
import { useAllowedCustomEvents } from 'utils/hooks/useCustomEvents';
import { Custom16, Purchase16 } from 'ui/icons/aurora/16px';
import { GoalEventType } from 'modules/goals/types';
import { EVENT_TYPE_DELIMITER, PURCHASE_EVENT_NAME } from '../CreateGoalForm.constants';

const makeOption = ({
  event,
  label,
  icon,
  suffix,
  eventType,
}: {
  event: string;
  label: string;
  icon: ReactNode;
  suffix: string;
  eventType: GoalEventType;
}): BaseOption => ({
  label,
  id: event,
  value: `${eventType}${EVENT_TYPE_DELIMITER}${event}`,
  Prefix: icon,
  Suffix: suffix,
});

const EXCLUDED_SYSTEM_EVENTS = [
  ClientDataType.CustomEvent,
  ClientDataType.User,
  ClientDataType.Purchase,
  ...Object.values(EXCLUDED_WHATSAPP_EVENTS),
];

const isPurchaseEvent = (eventName: string): boolean => eventName === PURCHASE_EVENT_NAME;

const getEventIcon = (eventName: string): ReactNode =>
  isPurchaseEvent(eventName) ? <Purchase16 /> : <Custom16 />;

const getEventSuffix = (eventName: string): string =>
  isPurchaseEvent(eventName) ? 'System event' : 'Custom event';

const getEventType = (eventName: string): GoalEventType =>
  isPurchaseEvent(eventName) ? GoalEventType.Purchase : GoalEventType.CustomEvent;

const sortOptionsByValue = (a: BaseOption, b: BaseOption): number => a.value.localeCompare(b.value);

export const useCombinedEventOptions = () => {
  const customEvents = useAllowedCustomEvents();
  const filterNonFeatureFlaggedEvents = useFilterNonFeatureFlaggedEvents();
  const { t } = useTranslation();

  const systemEventOptions = [...Object.values(ClientDataType)]
    .filter((event) => !EXCLUDED_SYSTEM_EVENTS.includes(event))
    .filter(filterNonFeatureFlaggedEvents)
    .map((event) =>
      makeOption({
        event,
        label: t(`v2.requirementBuilder.eventName.${event}`),
        icon: DATA_TYPE_TO_ICON[event],
        suffix: 'System event',
        eventType: GoalEventType.System,
      }),
    );

  const customAndPurchaseEventOptions = customEvents.map((eventName) =>
    makeOption({
      event: eventName,
      label: eventName,
      icon: getEventIcon(eventName),
      suffix: getEventSuffix(eventName),
      eventType: getEventType(eventName),
    }),
  );

  const options = useMemo(
    () => [...systemEventOptions, ...customAndPurchaseEventOptions].sort(sortOptionsByValue),
    [systemEventOptions, customAndPurchaseEventOptions],
  );

  return { options };
};
